<template>
  <aside class="panel">
    <template v-if="!selection.ids.length">
      <div class="text-h6 font-weight-bold">
        {{ section.name }}
      </div>
      <div class="text-body-2 text-medium-emphasis mb-3">
        {{ sectorName }}
      </div>

      <div v-for="status in statuses" :key="status" class="panel__count">
        <seat-status-swatch :status="status" />
        <span class="flex-grow-1">{{ SeatStatusLabels[status] }}</span>
        <strong>{{ counts[status] }}</strong>
      </div>

      <div class="text-caption text-medium-emphasis mt-4">
        Clique num lugar para selecioná-lo. Arraste para selecionar uma área, Shift+clique para ir até outro lugar,
        Ctrl/Cmd+clique para somar ou tirar um. A letra da fila seleciona a fila. Vendidos e reservados não entram na
        seleção.
      </div>

      <v-btn
        block
        variant="tonal"
        class="text-none mt-4"
        prepend-icon="fa-solid fa-lock"
        :disabled="!counts.blocked"
        @click="selection.selectStatus(SeatStatus.BLOCKED)"
      >
        Selecionar {{ counts.blocked === 1 ? 'o bloqueado' : `os ${counts.blocked} bloqueados` }}
      </v-btn>
    </template>

    <template v-else>
      <div class="text-h6 font-weight-bold">
        {{ plural(selection.ids.length, 'lugar selecionado', 'lugares selecionados') }}
      </div>
      <div class="text-caption text-medium-emphasis mb-3">
        {{ selectedNames }}
      </div>

      <v-btn
        block
        color="blue-grey-darken-3"
        class="text-none"
        prepend-icon="fa-solid fa-lock"
        :loading="applying === 'block'"
        :disabled="!selection.available.length || !!applying"
        @click="emit('block')"
      >
        Bloquear {{ plural(selection.available.length, 'disponível', 'disponíveis') }}
        <span class="panel__kbd">B</span>
      </v-btn>
      <v-btn
        block
        variant="outlined"
        class="text-none mt-2"
        prepend-icon="fa-solid fa-lock-open"
        :loading="applying === 'unblock'"
        :disabled="!selection.blocked.length || !!applying"
        @click="emit('unblock')"
      >
        Desbloquear {{ plural(selection.blocked.length, 'bloqueado', 'bloqueados') }}
        <span class="panel__kbd">D</span>
      </v-btn>
      <div v-if="selection.available.length && selection.blocked.length" class="text-caption text-medium-emphasis mt-2">
        A seleção tem os dois: cada botão só muda os lugares que estão no status de onde ele parte.
      </div>

      <v-btn
        block
        variant="text"
        class="text-none mt-3"
        @click="selection.clear()"
      >
        Limpar seleção <span class="panel__kbd">Esc</span>
      </v-btn>
    </template>

    <div v-if="selection.skipped" class="text-caption text-warning mt-3">
      {{ plural(selection.skipped, 'lugar vendido ou reservado ficou', 'lugares vendidos ou reservados ficaram') }}
      de fora da seleção.
    </div>
  </aside>
</template>

<script lang="ts" setup>
// The seat blocking page's side panel (IDV-705): the open Section's counts when nothing is selected; otherwise the
// selection and the two buttons that block its available seats or unblock its blocked ones, right away.
import { computed } from 'vue'
import { SeatStatus, SeatStatusLabels } from '@/constants/SeatStatuses'
import { joinNames, plural, type BoardSection, type SeatBoard, type StatusCounts } from '@/helpers/seatBlocking/board'
import type { SeatAction, SeatSelection } from '@/helpers/seatBlocking/selection'
import SeatStatusSwatch from './seat-status-swatch.vue'

interface Props {
  board: SeatBoard
  section: BoardSection
  sectorName: string
  selection: SeatSelection
  /** The change being sent, if any */
  applying: SeatAction | null
}

const { board, section, sectorName, selection, applying } = defineProps<Props>()

const emit = defineEmits<{
  block: []
  unblock: []
}>()

defineOptions({ name: 'SeatBlockingPanel' })

const statuses: SeatStatus[] = Object.values(SeatStatus)

const counts = computed<StatusCounts>(() => board.counts(section.id))

const selectedNames = computed<string>(() => joinNames(selection.ids.map(board.label), 10))
</script>

<style scoped>
.panel {
  width: 280px;
  flex: none;
  padding-left: 16px;
  border-left: 1px solid rgba(0, 0, 0, 0.08);
}

.panel__count {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 3px 0;
  font-size: 14px;
}

.panel__kbd {
  margin-left: 8px;
  padding: 1px 5px;
  border: 1px solid currentColor;
  border-radius: 4px;
  font: 600 10px/1.3 ui-monospace, Menlo, monospace;
  opacity: 0.6;
}
</style>
